import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import {
  Loader2, TrendingUp, TrendingDown, Minus, Edit, Check, GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { CATEGORY_LABELS, getPrimaryCategory } from './candidateHelpers';

/**
 * SalaryBenchmarkCard — Prétentions salariales + benchmark marché.
 *
 * Affiche :
 *  - le salaire souhaité du candidat (local + EUR équivalent),
 *  - une barre visuelle avec min / médian / max du marché,
 *  - la position relative du candidat sur la fourchette,
 *  - un éditeur inline avec garde-fou anti-typo (< 50% ou > 200% de la fourchette).
 */
const SalaryBenchmarkCard = ({ candidate, candidateId, onUpdated }) => {
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editLocal, setEditLocal] = useState('');
  const [editContract, setEditContract] = useState('full');
  const [editPartial, setEditPartial] = useState('');

  useEffect(() => {
    setEditLocal(candidate?.expected_salary_local || '');
    setEditContract(candidate?.contract_type || 'full');
    setEditPartial(candidate?.partial_percentage || '');
  }, [candidate]);

  useEffect(() => {
    const fetchBenchmark = async () => {
      if (!candidateId || !candidate?.country) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/salary-benchmark/${candidateId}`);
        if (response.ok) {
          const data = await response.json();
          setBenchmark(data);
        } else {
          setError('Impossible de charger le benchmark');
        }
      } catch (err) {
        console.error('Benchmark fetch error:', err);
        setError('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };

    fetchBenchmark();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, candidate?.country, candidate?.primary_category, candidate?.years_of_experience]);

  // If no salary expectations, show simplified view
  const hasSalaryExpectation = candidate?.expected_salary_local || candidate?.expected_salary_eur;

  if (loading) {
    return (
      <Card className="p-6 border-tp-border">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-tp-text-light" />
          <span className="ml-2 text-tp-text-light">Chargement du benchmark...</span>
        </div>
      </Card>
    );
  }

  // No salary data at all - STILL show benchmark if available
  if (!hasSalaryExpectation && (!benchmark || !benchmark.data_available)) {
    return (
      <Card className="p-4 md:p-6 border-tp-border" data-testid="salary-benchmark-card">
        <h3 className="text-base md:text-lg font-display font-semibold text-tp-text mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
          Prétentions salariales & Benchmark
        </h3>
        <p className="text-tp-text-light text-sm">
          Aucune donnée de benchmark disponible pour ce profil ({candidate?.country || 'pays inconnu'} / {candidate?.primary_category || candidate?.job_category || 'catégorie inconnue'}).
        </p>
      </Card>
    );
  }

  const getStatusColor = (color) => {
    switch (color) {
      case 'green': return 'text-tp-accent bg-tp-accent/10 border-tp-accent/30';
      case 'orange': return 'text-tp-primary bg-tp-primary/10 border-tp-primary/30';
      case 'red': return 'text-tp-primary bg-tp-primary/5 border-tp-primary/20';
      default: return 'text-tp-text-light bg-tp-bg border-tp-border';
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'within_market' || status === 'slightly_below') {
      return <TrendingUp className="h-4 w-4" />;
    }
    if (status === 'above_market' || status === 'slightly_above') {
      return <TrendingDown className="h-4 w-4" />;
    }
    if (status === 'below_market') {
      return <Minus className="h-4 w-4" />;
    }
    return null;
  };

  const handleSaveSalary = async () => {
    const localVal = parseFloat(editLocal);
    if (!localVal || localVal <= 0) {
      toast.error('Montant invalide');
      return;
    }

    const actualAmount = editContract === 'partial' && editPartial
      ? (localVal * parseFloat(editPartial)) / 100
      : localVal;

    const minBench = benchmark?.salary_local?.min ?? benchmark?.benchmark_local?.min ?? null;
    const maxBench = benchmark?.salary_local?.max ?? benchmark?.benchmark_local?.max ?? null;
    const currency = candidate?.currency || benchmark?.salary_local?.currency || '';

    if (minBench && maxBench) {
      const UNDER_THRESHOLD = 0.5;
      const OVER_THRESHOLD = 2.0;
      let warning = null;
      if (actualAmount < minBench * UNDER_THRESHOLD) {
        warning = `Salaire très en-dessous du marché : ${actualAmount.toLocaleString('fr-FR')} ${currency}\n` +
          `Fourchette du marché : ${minBench.toLocaleString('fr-FR')} — ${maxBench.toLocaleString('fr-FR')} ${currency}\n\n` +
          `Avez-vous oublié un zéro ? Êtes-vous sûr de vouloir enregistrer ce montant ?`;
      } else if (actualAmount > maxBench * OVER_THRESHOLD) {
        warning = `Salaire très au-dessus du marché : ${actualAmount.toLocaleString('fr-FR')} ${currency}\n` +
          `Fourchette du marché : ${minBench.toLocaleString('fr-FR')} — ${maxBench.toLocaleString('fr-FR')} ${currency}\n\n` +
          `Y a-t-il un zéro en trop ? Êtes-vous sûr de vouloir enregistrer ce montant ?`;
      }
      if (warning && !window.confirm(warning)) {
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        expected_salary_local: localVal,
        currency: candidate?.currency || '',
        contract_type: editContract,
        partial_percentage: editContract === 'partial' ? parseFloat(editPartial) || null : null,
      };
      const res = await api.patch(`/admin/candidates/${candidateId}/profile`, payload);
      if (res.ok) {
        toast.success('Prétentions mises à jour');
        setEditing(false);
        if (onUpdated) onUpdated();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch (_) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 md:p-6 border-tp-border" data-testid="salary-benchmark-card">
      <div className="flex items-center justify-between mb-4 md:mb-6 gap-2 flex-wrap">
        <h3 className="text-base md:text-lg font-display font-semibold text-tp-text flex items-center gap-2">
          <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-tp-accent" />
          Prétentions salariales & Benchmark marché
        </h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs px-3 py-1.5 border border-gray-200 hover:border-tp-accent hover:text-tp-accent rounded-md flex items-center gap-1 text-gray-600 transition-colors"
            data-testid="edit-salary-btn"
          >
            <Edit className="h-3 w-3" />
            Modifier
          </button>
        )}
      </div>

      {/* Edit mode — live validation against market benchmark */}
      {editing && (() => {
        const localVal = parseFloat(editLocal);
        const actualAmount = editContract === 'partial' && editPartial
          ? (localVal * parseFloat(editPartial || 0)) / 100
          : localVal;
        const minBench = benchmark?.salary_local?.min ?? benchmark?.benchmark_local?.min ?? null;
        const maxBench = benchmark?.salary_local?.max ?? benchmark?.benchmark_local?.max ?? null;
        const currency = candidate?.currency || benchmark?.salary_local?.currency || '';
        let rangeWarning = null;
        if (actualAmount && minBench && maxBench) {
          if (actualAmount < minBench * 0.5) {
            rangeWarning = { type: 'under', text: `⚠️ Très en-dessous du marché (${minBench.toLocaleString('fr-FR')}—${maxBench.toLocaleString('fr-FR')} ${currency}). Vérifiez un zéro manquant ?` };
          } else if (actualAmount > maxBench * 2) {
            rangeWarning = { type: 'over', text: `⚠️ Très au-dessus du marché (${minBench.toLocaleString('fr-FR')}—${maxBench.toLocaleString('fr-FR')} ${currency}). Vérifiez un zéro en trop ?` };
          } else if (actualAmount < minBench * 0.8 || actualAmount > maxBench * 1.2) {
            rangeWarning = { type: 'hint', text: `Hors fourchette marché (${minBench.toLocaleString('fr-FR')}—${maxBench.toLocaleString('fr-FR')} ${currency})` };
          }
        }
        return (
          <div className="mb-5 p-4 bg-tp-primary/5 border border-tp-primary/20 rounded-lg" data-testid="salary-edit-form">
            <p className="text-sm font-medium text-tp-text mb-3">Modifier les prétentions salariales</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-tp-text-light block mb-1">
                  Montant mensuel local ({candidate?.currency || 'devise manquante'})
                </label>
                <input
                  type="number"
                  min="1"
                  step="1000"
                  value={editLocal}
                  onChange={(e) => setEditLocal(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none ${
                    rangeWarning?.type === 'under' || rangeWarning?.type === 'over'
                      ? 'border-red-400 focus:border-red-500 bg-red-50/40'
                      : rangeWarning?.type === 'hint'
                        ? 'border-orange-300 focus:border-orange-400 bg-orange-50/40'
                        : 'border-gray-200 focus:border-tp-accent'
                  }`}
                  placeholder="Ex: 500000"
                  data-testid="salary-edit-local-input"
                />
                {rangeWarning ? (
                  <p
                    className={`text-[11px] mt-1 ${
                      rangeWarning.type === 'hint' ? 'text-orange-600' : 'text-red-600 font-medium'
                    }`}
                    data-testid="salary-range-warning"
                  >
                    {rangeWarning.text}
                  </p>
                ) : (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Saisir le montant dans la monnaie locale du pays ({candidate?.country || '—'}). La conversion EUR/MGA sera recalculée automatiquement.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-tp-text-light block mb-1">Type de contrat</label>
                <select
                  value={editContract}
                  onChange={(e) => setEditContract(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-tp-accent"
                  data-testid="salary-edit-contract-select"
                >
                  <option value="full">Temps complet (100%)</option>
                  <option value="partial">Temps partiel</option>
                </select>
              </div>
              {editContract === 'partial' && (
                <div>
                  <label className="text-xs text-tp-text-light block mb-1">Pourcentage du temps</label>
                  <input
                    type="number"
                    min="10"
                    max="99"
                    step="5"
                    value={editPartial}
                    onChange={(e) => setEditPartial(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-tp-accent"
                    placeholder="Ex: 50"
                    data-testid="salary-edit-partial-input"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSaveSalary}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-tp-accent hover:bg-tp-accent/90 text-white rounded-md flex items-center gap-1 disabled:opacity-60"
                  data-testid="salary-edit-save-btn"
                >
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Enregistrer
                </button>
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Candidate's salary expectation */}
      {hasSalaryExpectation ? (
        <div className="mb-6">
          <p className="text-sm font-medium text-tp-text mb-3">Prétentions du candidat (mensuel)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {candidate.expected_salary_local && candidate.currency && (
              <div className="p-4 bg-tp-primary/10 rounded-lg border border-tp-primary/30">
                <p className="text-xs text-tp-text-light mb-1">
                  {candidate.contract_type === 'partial' && candidate.partial_percentage
                    ? `Temps partiel ${candidate.partial_percentage}%`
                    : 'Temps complet 100%'
                  }
                </p>
                <p className="text-2xl font-semibold text-tp-primary">
                  {Number(candidate.expected_salary_local).toLocaleString('fr-FR')} {candidate.currency}
                </p>
                <p className="text-xs text-tp-text-light mt-1">/ mois</p>
              </div>
            )}
            {candidate.expected_salary_eur && (
              <div className="p-4 bg-gray-100 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Équivalent EUR (interne)</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {Number(candidate.expected_salary_eur).toLocaleString('fr-FR')} EUR
                </p>
                <p className="text-xs text-gray-600 mt-1">/ mois</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-tp-primary/5 rounded-lg border border-tp-primary/15">
          <p className="text-sm text-tp-primary">Salaire souhaité non renseigné par le candidat</p>
        </div>
      )}

      {/* Market Benchmark */}
      {benchmark?.data_available && (
        <>
          <Separator className="my-4" />

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-tp-text">
                Benchmark marché ({benchmark.salary_local?.currency || benchmark.currency?.symbol || 'EUR'})
              </p>
              <Badge variant="outline" className="text-xs">
                {benchmark.experience_level_label || benchmark.experience_level}
              </Badge>
            </div>
            <p className="text-xs text-tp-text-light">
              Fourchette salariale pour {benchmark.country} • {CATEGORY_LABELS[benchmark.category_used] || benchmark.category_used || benchmark.job_category}
            </p>
          </div>

          {/* Visual benchmark bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-tp-text-light mb-2">
              <span>Min</span>
              <span>Médian</span>
              <span>Max</span>
            </div>

            {/* Benchmark range in local currency */}
            <div className="relative h-14 bg-gradient-to-r from-tp-accent/20 via-tp-accent/40 to-tp-accent/20 rounded-lg mb-2">
              <div className="absolute left-0 top-0 h-full flex flex-col justify-center px-2">
                <span className="text-xs font-semibold text-tp-text">
                  {(benchmark.salary_local?.min || benchmark.benchmark_local?.min || 0).toLocaleString('fr-FR')} {benchmark.salary_local?.currency || ''}
                </span>
              </div>

              <div className="absolute left-1/2 transform -translate-x-1/2 top-0 h-full flex flex-col justify-center text-center">
                <div className="w-px h-6 bg-tp-accent mx-auto"></div>
                <span className="text-xs font-semibold text-tp-accent">
                  {(benchmark.salary_local?.median || benchmark.benchmark_local?.median || 0).toLocaleString('fr-FR')} {benchmark.salary_local?.currency || ''}
                </span>
              </div>

              <div className="absolute right-0 top-0 h-full flex flex-col justify-center px-2 text-right">
                <span className="text-xs font-semibold text-tp-text">
                  {(benchmark.salary_local?.max || benchmark.benchmark_local?.max || 0).toLocaleString('fr-FR')} {benchmark.salary_local?.currency || ''}
                </span>
              </div>

              {/* Candidate position indicator */}
              {benchmark.comparison && (
                <div
                  className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2"
                  style={{
                    left: `${Math.max(5, Math.min(95, benchmark.comparison.position_percent))}%`
                  }}
                >
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    benchmark.comparison.color === 'green' ? 'bg-tp-accent border-white' :
                      benchmark.comparison.color === 'orange' ? 'bg-tp-primary border-white' :
                        'bg-tp-primary/60 border-white'
                  } shadow-lg`}></div>
                </div>
              )}
            </div>

            {/* EUR equivalent (interne) */}
            <div className="flex justify-between text-xs text-tp-text-light">
              <span>{(benchmark.salary_eur?.min || benchmark.benchmark_eur?.min || 0).toLocaleString('fr-FR')} EUR</span>
              <span>{(benchmark.salary_eur?.median || benchmark.benchmark_eur?.median || 0).toLocaleString('fr-FR')} EUR</span>
              <span>{(benchmark.salary_eur?.max || benchmark.benchmark_eur?.max || 0).toLocaleString('fr-FR')} EUR</span>
            </div>
          </div>

          {/* Comparison result */}
          {benchmark.comparison && (
            <div className={`p-4 rounded-lg border ${getStatusColor(benchmark.comparison.color)}`}>
              <div className="flex items-center gap-3">
                {getStatusIcon(benchmark.comparison.status)}
                <div>
                  <p className="font-medium">{benchmark.comparison.status_label}</p>
                  <p className="text-xs opacity-80">
                    {benchmark.comparison.is_competitive
                      ? 'Les attentes salariales sont compétitives pour ce profil.'
                      : benchmark.comparison.status === 'above_market'
                        ? 'Les attentes sont au-dessus de la fourchette marché.'
                        : 'Les attentes sont en dessous de la fourchette marché.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Education bonus note */}
          {benchmark.has_relevant_education && (
            <p className="text-xs text-tp-text-light mt-4 flex items-center gap-1">
              <GraduationCap className="h-3 w-3" />
              Bonus formation appliqué (+15%) - Formation pertinente détectée
            </p>
          )}
        </>
      )}

      {/* No benchmark data but has salary */}
      {!benchmark?.data_available && hasSalaryExpectation && (
        <div className="mt-4 p-4 bg-tp-primary/5 border border-tp-primary/15 rounded-lg">
          <p className="text-sm text-tp-primary">
            <span className="font-medium">Benchmark non disponible</span>
            <br />
            <span className="text-tp-primary/70">Données de référence non disponibles pour {candidate.country || 'ce pays'} / {CATEGORY_LABELS[getPrimaryCategory(candidate)] || 'cette catégorie'}.</span>
          </p>
        </div>
      )}
    </Card>
  );
};

export default SalaryBenchmarkCard;
